/**
 * Detect the signed-in user's browser timezone and PATCH it to the server
 * once per session when it differs from the stored value.
 *
 * Why a client effect: the Monday weekly-report email cron
 * (`src/inngest/sendWeeklyReportEmails.ts`) fires per owner at *their*
 * local 8am. We have no other reliable signal for a user's IANA tz —
 * Clerk doesn't expose one, and the server can't infer it from headers.
 *
 * Idempotent: the tRPC `user.setTimezone` mutation no-ops when the value
 * is unchanged, and a ref guards re-fires within a single mount.
 */
"use client";

import { useEffect, useRef } from "react";
import { useUser } from "@clerk/nextjs";

import { api } from "@/lib/trpc/react";

export function TimezoneCapture() {
  const { isSignedIn, user } = useUser();
  const sentForUserId = useRef<string | null>(null);

  const { data: me } = api.user.getMe.useQuery(undefined, {
    enabled: Boolean(isSignedIn && user?.id),
    staleTime: 5 * 60 * 1000,
  });

  // Destructure `.mutate` so the dep array gets a stable reference — the
  // mutation *object* is a fresh identity every render (see CLAUDE.md
  // best-practices section + the local-rules/no-mutation-object-in-deps lint).
  const { mutate: setTimezoneMutate } = api.user.setTimezone.useMutation();

  useEffect(() => {
    if (!isSignedIn || !user?.id) return;
    if (sentForUserId.current === user.id) return;
    if (!me) return; // wait for the query to resolve

    let detected: string | null = null;
    try {
      detected = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
      /* benign: timezone detection is optional; app works without it */
      return;
    }
    if (!detected) return;
    if (detected === me.timezone) {
      sentForUserId.current = user.id;
      return;
    }

    // Optimistically claim the in-flight slot so re-renders while the
    // mutation is pending don't fire it again. On error, release the
    // claim so the next effect run (auth change / `me` refetch) retries —
    // otherwise a transient 5xx or offline blip would lock this user out
    // of having their timezone stored for the rest of the session.
    sentForUserId.current = user.id;
    setTimezoneMutate(
      { timezone: detected },
      {
        onError: () => {
          if (sentForUserId.current === user.id) {
            sentForUserId.current = null;
          }
        },
      },
    );
  }, [isSignedIn, user?.id, me, setTimezoneMutate]);

  return null;
}
